import Link from "next/link";
import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import { initializeSocket } from "@/utils/socket";
import { formatDate as formatDateUtil } from "@/utils/formatDate";

const ADMIN_TYPES = [
  "ADMIN_NEW_INSTRUCTOR_REGISTRATION",
  "ADMIN_NEW_HR_REGISTRATION",
  "ADMIN_NEW_CANDIDATE_REGISTRATION",
  "ADMIN_NEW_BOOKING_REQUEST",
];

const AdminNotifications = () => {
  const router = useRouter();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const notificationRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const user =
    typeof window !== "undefined"
      ? JSON.parse(localStorage.getItem("user") || "null")
      : null;
  const userId = user?._id || user?.id;

  const typeFilter = ADMIN_TYPES.join(",");

  const totalUnreadCount = notifications.length;

  const getNotificationRoute = (notification) => {
    switch (notification?.type) {
      case "ADMIN_NEW_INSTRUCTOR_REGISTRATION":
        return "/admins/approvals?type=instructor";
      case "ADMIN_NEW_HR_REGISTRATION":
        return "/admins/approvals?type=hr";
      case "ADMIN_NEW_CANDIDATE_REGISTRATION":
        return "/admins/approvals?type=candidate";
      case "ADMIN_NEW_BOOKING_REQUEST":
        return "/admins/bookings?status=pending";
      default:
        return "/admins/admin-dashboard";
    }
  };

  const fetchAdminNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError("");
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/notifications?type=${encodeURIComponent(typeFilter)}&isRead=false&page=1&limit=25`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load admin notifications");
      }

      const data = await response.json();
      setNotifications(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      setError(err?.message || "Failed to load admin notifications");
    } finally {
      setLoading(false);
    }
  }, [userId, typeFilter]);

  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setOverlayOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchAdminNotifications();
  }, [userId, fetchAdminNotifications]);

  useEffect(() => {
    if (!userId) return;
    const socket = initializeSocket(userId);

    const handleNewNotification = (notification) => {
      if (!ADMIN_TYPES.includes(notification?.type)) return;
      if (notification?.isRead) return;

      setNotifications((prev) => [notification, ...prev]);
    };

    socket.on("new-notification", handleNewNotification);
    return () => {
      socket.off("new-notification", handleNewNotification);
    };
  }, [userId]);

  const handleBellClick = (event) => {
    event.preventDefault();
    setOverlayOpen((prev) => !prev);
    if (!overlayOpen) {
      fetchAdminNotifications();
    }
  };

  const handleMarkAsReadAndOpen = async (notification) => {
    try {
      await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/notifications/${notification._id}/read`,
        {
          method: "PATCH",
          credentials: "include",
        }
      );
    } catch (err) {
      // Keep UX resilient even if mark-as-read fails.
    }

    setNotifications((prev) => prev.filter((n) => n._id !== notification._id));
    setOverlayOpen(false);
    router.push(getNotificationRoute(notification));
  };

  return (
    <li
      className="access-icon rbt-notification-wrapper rbt-notification-position"
      ref={notificationRef}
    >
      <Link
        className="rbt-round-btn rbt-notification-btn"
        href="#"
        onClick={handleBellClick}
        title="Admin Notifications"
      >
        <i className="feather-bell"></i>
        {totalUnreadCount > 0 && (
          <span className="rbt-notification-count">
            {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
          </span>
        )}
      </Link>

      {overlayOpen && (
        <div className="rbt-job-notification-overlay" style={{ minWidth: "330px" }}>
          <div className="rbt-job-overlay-header d-flex justify-content-between align-items-center">
            <h6 className="rbt-job-overlay-title mb-0">Admin Notifications</h6>
            <button
              type="button"
              className="btn btn-sm p-0 d-inline-flex align-items-center justify-content-center"
              onClick={fetchAdminNotifications}
              aria-label="Refresh notifications"
              title="Refresh"
              style={{
                width: "26px",
                height: "26px",
                borderRadius: "50%",
                border: "1px solid rgba(37, 99, 235, 0.25)",
                color: "#2563eb",
                backgroundColor: "rgba(37, 99, 235, 0.08)",
              }}
            >
              <i className="feather-refresh-cw" style={{ fontSize: "14px" }}></i>
            </button>
          </div>

          {error && <div className="p-3 text-danger small">{error}</div>}

          {loading ? (
            <div className="p-3">Loading...</div>
          ) : (
            <>
              <ul className="rbt-job-overlay-list">
                {notifications.map((notification) => (
                  <li key={notification._id} className="rbt-job-overlay-item unread">
                    <button
                      type="button"
                      onClick={() => handleMarkAsReadAndOpen(notification)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        textAlign: "left",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      <div>
                        <div className="rbt-job-overlay-title-text">{notification.title}</div>
                        <div className="small text-muted">{notification.message}</div>
                        <div className="small text-muted">{formatDateUtil(notification.createdAt)}</div>
                      </div>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: "rgba(37, 99, 235, 0.16)",
                          color: "#1d4ed8",
                          border: "1px solid rgba(37, 99, 235, 0.25)",
                          borderRadius: "999px",
                          padding: "6px 10px",
                          lineHeight: 1,
                          alignSelf: "flex-start",
                          minWidth: "auto",
                        }}
                      >
                        New
                      </span>
                    </button>
                  </li>
                ))}
              </ul>

              {totalUnreadCount === 0 && (
                <div className="p-3 text-center text-muted">
                  No new incoming requests
                </div>
              )}

              <div className="rbt-job-overlay-footer">
                <Link
                  href="/admins/notifications"
                  className="rbt-job-overlay-view-all"
                  onClick={() => setOverlayOpen(false)}
                >
                  View All Notifications →
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
};

export default AdminNotifications;
